import React, { useState, useEffect } from 'react';
import { Typography, Dropdown, Menu, Modal } from 'antd';
import styled from 'styled-components';
import { Icon } from 'ensightful-koala-ui';

import EditAccount from './EditAccount';
import userRequests from 'services/user';
import adminRequests from 'services/admin';
import groupStatusesRequests from 'services/groupStatuses';
import instructorWeeklyReportsRequests from 'services/instructorWeeklyReports';
import { Text } from '../CreateCourse/design';
import { PrimaryButton } from 'components/molecules/Button';
import { InputWithBorder as InputField } from 'components/molecules/TextInput';
import { message } from 'shell/components';

import icPen from 'utils/assetFiles/svgs/icPen';
import icMenu from 'utils/assetFiles/svgs/icMenu';
import icSortUp from 'utils/assetFiles/svgs/icSortUp';
import icSortDown from 'utils/assetFiles/svgs/icSortDown';
import icPageAfter from 'utils/assetFiles/svgs/icPageAfter';
import icPageBefore from 'utils/assetFiles/svgs/icPageBefore';

const { Title } = Typography;

const Container = styled.div`
    background-color: #fafafa;
    display: flex;
    justify-content: center;
    min-height: 100%;
    padding: 30px;
    width: 100%;
`;

const ContentWrapper = styled.div`
    display: grid;
    grid-auto-rows: auto auto 1fr;
`;

const ActionsContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-bottom: 8px;

    & button {
        height: 40px;
    }
`;

const ListWrapper = styled.div`
    background-color: #fff;
    padding: 20px;
    min-width: 800px;
    max-width: 900px;
    border-radius: 12px;
    display: grid;
    grid-auto-rows: auto 1fr;
`;

const ListHeader = styled.div`
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    border-bottom: 1px solid #eeeeee;
    padding: 10px;
`;

const ContentRowWrapper = styled.div`
    position: relative;
`;

const ContentRows = styled.div`
    overflow: auto;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
`;

const ContentRow = styled.div`
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    border-bottom: 1px solid #eeeeee;
    padding: 10px;
    :hover {
        background-color: #efedff;
        border-radius: 6px;
    }
`;

const OptionWrapper = styled.div`
    float: right;
    padding: 0px 5px;
    border-radius: 3px;
    :hover {
        background: #c0c0c0;
    }
`;

const PagesWrapper = styled.div`
    display: flex;
    gap: 0.5em;
`;

const NumberInput = styled.input`
    width: 40px;
    -moz-appearance: textfield !important;
    ::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
    ::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
`;

const HeaderContainer = styled(Text)`
    color: #757575;
`;

const HeaderItem = ({ children, sort, onClick, text }) => {
    const SortWrapper = styled(Text)`
        display: inline-block;
        cursor: pointer;
        color: #757575;
    `;

    const SortIcons = styled(Text)`
        display: inline-block;
        margin-left: 3px;
    `;

    return (
        <HeaderContainer>
            <SortWrapper onClick={onClick}>
                {text}
                <SortIcons>{sort ? icSortUp : icSortDown}</SortIcons>
            </SortWrapper>
        </HeaderContainer>
    );
};

const Pagenation = ({ current, total, onClickBefore, onClickAfter, onGoTo }) => {
    const SortWrapper = styled(Text)`
        display: inline-block;
        color: #757575;
        text-align: right;
    `;

    const SortIcons = styled(Text)`
        display: inline-block;
        margin-left: 15px;
        padding: 0px 5px;
        cursor: pointer;
    `;

    return (
        <HeaderContainer>
            <SortWrapper>
                <PagesWrapper>
                    <NumberInput type={'number'} min={'1'} max={total} value={current} onChange={onGoTo} />
                    {`of ${total} Pages`}
                </PagesWrapper>
                <SortIcons onClick={onClickBefore}>{icPageBefore}</SortIcons>
                <SortIcons onClick={onClickAfter}>{icPageAfter}</SortIcons>
            </SortWrapper>
        </HeaderContainer>
    );
};

const SearchWrapper = styled.div`
    & > div {
        height: auto;
        padding: 0;
    }

    & p {
        margin: 0;
    }
`;

const Cell = styled(Text)``;

const CellEnd = styled.div`
    justify-self: end;
    padding: 0px 8px;
`;

const CountPerPage = 20;

let allUsers = [];

const AllAccounts = () => {
    const [sort, setSort] = useState();
    const [current, setCurrent] = useState(1);
    const [editData, setEditData] = useState({});
    const [editModal, setEditModal] = useState(false);
    const [users, setUsers] = useState([]);
    const [searchInput, setSearchInput] = useState('');

    const handleSubmitSave = async ({ id, ...data }) => {
        try {
            // const user = await postRequest(ADMIN.UPDATE_USER, data, { userId: id });
            const user = await userRequests.updateUser(id, data);
            const filteredUsers = users.map((u) => (u.id === user.id ? user : u));
            setUsers(filteredUsers);
            setEditModal(false);
            allUsers = allUsers.map((u) => (u.id === user.id ? user : u));
        } catch (err) {
            message.error('An error was occurred. Please contact system administrator.');
        }
    };

    const closeEdit = () => {
        setEditModal(false);
    };

    const handleOpenEdit = (user) => {
        setEditData(user);
        setEditModal(true);
    };

    const handleClickHeader = (column) => {
        if (sort === column) {
            setUsers(users.map((i) => i).sort((a, b) => (a[column] || '').localeCompare(b[column] || '')));
            setSort();
            return;
        }

        setUsers(users.map((i) => i).sort((a, b) => ((a[column] || '').localeCompare(b[column] || '') === 1 ? -1 : 1)));
        setSort(column);
    };

    const handleClickBefore = () => {
        let temp = current;
        temp -= 1;
        if (temp <= 0) {
            return;
        }

        setCurrent(temp);
    };

    const handleClickAfter = () => {
        let temp = current;
        temp += 1;
        if (temp > Math.ceil(users.length / CountPerPage)) {
            return;
        }

        setCurrent(temp);
    };

    const handleGoTo = (val) => {
        if (val.target.value > Math.ceil(users.length / CountPerPage)) {
            return;
        }
        setCurrent(val.target.value);
    };

    const handleChangeSearchInput = (e) => {
        const val = e.toLowerCase() || '';
        setSearchInput(val);
        setCurrent(1);

        if (val === '') {
            setUsers(allUsers);
            return;
        }

        setUsers(
            allUsers.filter(
                (u) =>
                    (u.studentId || '').toLowerCase().includes(val) ||
                    (u.name || '').toLowerCase().includes(val) ||
                    (u.username || '').toLowerCase().includes(val) ||
                    (u.password || '').toLowerCase().includes(val) ||
                    (u.role || '').toLowerCase().includes(val)
            )
        );
    };

    const updateSuggestionsData = async () => {
        try {
            message.loading({ key: 'suggestions', duration: 0, content: 'Processing...' });
            await groupStatusesRequests.updateSuggestionsData();
            message.success({ key: 'suggestions', duration: 2, content: 'Suggestions data updated.' });
        } catch (e) {
            console.error(e);
            message.error({
                key: 'suggestions',
                duration: 2,
                content: 'Failed to update suggestions data.',
            });
        }
    };

    const sendWeeklyReports = async () => {
        try {
            message.loading({ key: 'reports', duration: 0, content: 'Sending...' });
            await instructorWeeklyReportsRequests.sendWeeklyReports();
            message.success({ key: 'reports', duration: 2, content: 'Weekly Reports Sent.' });
        } catch (e) {
            console.error(e);
            message.error({
                key: 'reports',
                duration: 2,
                content: 'One or more weekly reports failed to send.',
            });
        }
    };

    useEffect(() => {
        const findUsers = async () => {
            const users = await adminRequests.listAllUsers({});
            setUsers(users);
            allUsers = users;
        };
        findUsers();
    }, []);

    return (
        <>
            <Container>
                <ContentWrapper>
                    <Title level={3}>All Accounts</Title>
                    <ActionsContainer style={{ display: 'flex', gap: 8 }}>
                        {process.env.REACT_APP_PLATFORM !== 'production' && (
                            <>
                                <PrimaryButton
                                    active
                                    text={'Update Suggestions Data'}
                                    onClick={updateSuggestionsData}
                                />
                                <PrimaryButton active text={'Send Weekly Report Emails'} onClick={sendWeeklyReports} />
                            </>
                        )}
                        <SearchWrapper>
                            <InputField
                                suffix={<Icon kind='search' height='14' />}
                                allowClear
                                value={searchInput}
                                onChange={handleChangeSearchInput}
                                placeholder='Search by keyword'
                            />
                        </SearchWrapper>
                    </ActionsContainer>
                    <ListWrapper>
                        <ListHeader>
                            <HeaderItem
                                sort={sort === 'studentId'}
                                onClick={() => handleClickHeader('studentId')}
                                text='Student ID'
                            />
                            <HeaderItem sort={sort === 'name'} onClick={() => handleClickHeader('name')} text='Name' />
                            <HeaderItem
                                sort={sort === 'username'}
                                onClick={() => handleClickHeader('username')}
                                text='Username'
                            />
                            <HeaderItem
                                sort={sort === 'password'}
                                onClick={() => handleClickHeader('password')}
                                text='Password'
                            />
                            <HeaderItem sort={sort === 'role'} onClick={() => handleClickHeader('role')} text='Role' />
                            <Pagenation
                                current={current}
                                total={Math.ceil(users.length / CountPerPage)}
                                onClickBefore={handleClickBefore}
                                onClickAfter={handleClickAfter}
                                onGoTo={handleGoTo}
                            />
                        </ListHeader>

                        <ContentRowWrapper>
                            <ContentRows>
                                {users.slice((current - 1) * CountPerPage, current * CountPerPage).map((u) => {
                                    return (
                                        <ContentRow key={u.id || u._id}>
                                            <Cell>{u.studentId}</Cell>
                                            <Cell>{u.name}</Cell>
                                            <Cell>{u.username}</Cell>
                                            <Cell>{u.password}</Cell>
                                            <Cell>{u.role}</Cell>
                                            <CellEnd>
                                                <Dropdown
                                                    overlay={
                                                        <Menu onClick={({ domEvent }) => domEvent.stopPropagation()}>
                                                            <Menu.Item onClick={() => handleOpenEdit(u)}>
                                                                <span style={{ paddingRight: 10 }}>{icPen}</span>
                                                                <span>Edit</span>
                                                            </Menu.Item>

                                                            {/* <Menu.Item onClick={() => {}}>
                                  <span style={{paddingRight: 10}}>
                                    {icDelete}
                                  </span>
                                  <span>Delete</span>
                                </Menu.Item> */}
                                                        </Menu>
                                                    }
                                                    placement='bottomRight'
                                                    trigger={['click']}
                                                >
                                                    <OptionWrapper onClick={(e) => e.stopPropagation()}>
                                                        {icMenu}
                                                    </OptionWrapper>
                                                </Dropdown>
                                            </CellEnd>
                                        </ContentRow>
                                    );
                                })}
                            </ContentRows>
                        </ContentRowWrapper>
                    </ListWrapper>
                </ContentWrapper>
            </Container>

            <Modal key={Math.random()} centered visible={editModal} onCancel={closeEdit} footer={null}>
                <EditAccount data={editData} onSave={handleSubmitSave} onCancel={closeEdit} />
            </Modal>
        </>
    );
};

export default AllAccounts;
